/**
 * Hook for fetching vendor detail data
 */

import { useQuery } from '@tanstack/react-query';
import { getVendorDetail, vendorDetailKeys, VENDOR_DETAIL_QUERY_CONFIG } from '../requests/index';

/**
 * React Query hook for fetching vendor detail information
 *
 * @param vendorName - The name of the vendor to fetch details for, or undefined to disable
 * @returns React Query result with vendor detail data
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useVendorDetail('Acme Corporation');
 *
 * if (isLoading) return <Skeleton />;
 * if (error) return <ErrorState />;
 *
 * return <VendorDetailPage data={data.vendor_data} />;
 * ```
 */
export const useVendorDetail = (vendorName: string | undefined) => {
  return useQuery({
    queryKey: vendorDetailKeys.detail(vendorName || ''),
    queryFn: () => getVendorDetail(vendorName!),
    enabled: !!vendorName && vendorName.trim().length > 0,
    ...VENDOR_DETAIL_QUERY_CONFIG,
  });
};
